import React, { FC, Fragment } from 'react';
import { Button, Box, Autocomplete, TextField, Badge } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowDateFilter from 'components/shared/arrow-date-filter/arrow-date-filter.component';
import WarningIcon from '@mui/icons-material/Warning';
import { useQuery } from '@apollo/client';
import { departmentsTemplate, getClinicsForLabDropdownTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Department, Organization } from 'models/organization';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { DeliveryScheduleFilters, DeliveryScheduleFinishedFilter } from 'models/delivery-schedule';
import DeliveryScheduleStateFilter from 'components/delivery-schedule/delivery-schedule-state-filter.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type DeliverySchedulePageActionsProps = {
    filters: DeliveryScheduleFilters;
    onFiltersChange: (filters: DeliveryScheduleFilters) => void;
    onDateFilterChange: (date: Date | null) => void;
    selectedDate: Date | null;
    lateOrdersCount: number | null;
    isExternal?: boolean;
    noDeadlineOrdersCount: number | null;
    onLateOrdersShow: () => void;
    onNoDeadlineShow: () => void;
};

const DeliverySchedulePageActions: FC<DeliverySchedulePageActionsProps> = ({
    filters,
    onFiltersChange,
    onDateFilterChange,
    selectedDate,
    lateOrdersCount,
    noDeadlineOrdersCount,
    onLateOrdersShow,
    isExternal,
    onNoDeadlineShow,
}: DeliverySchedulePageActionsProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'delivery-schedule']);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    // The departments query rejects external technicians, so they get no department filter.
    const isExternalTechnician = permissions.isExternalTechnician(currentUser.role);

    const { data: { clinicsForLab } = {}, loading } = useQuery(getClinicsForLabDropdownTemplate, {
        variables: {
            filters: {
                isPickUpAndDeliveryPlace: true,
                showParents: true,
            },
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { departments } = {}, loading: departmentsLoading } = useQuery(departmentsTemplate, {
        skip: isExternalTechnician,
        onError(error) {
            showBoundary(error);
        },
    });

    const handleClinicChange = (_: React.SyntheticEvent, clinic: Organization | null) => {
        onFiltersChange({ ...filters, clinic });
    };

    const handleDepartmentChange = (_: React.SyntheticEvent, department: Department | null) => {
        onFiltersChange({ ...filters, department });
    };

    const handleStateChange = (finishedFilter: DeliveryScheduleFinishedFilter) => {
        onFiltersChange({ ...filters, finishedFilter });
    };

    return (
        <Fragment>
            <Box mb={1} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                <Box flexGrow={1} display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <ArrowDateFilter
                        dateValue={selectedDate || new Date()}
                        onDateFilterChange={onDateFilterChange}
                        label={translate('common:DATE')}
                    />

                    <Autocomplete
                        sx={{ minWidth: 320 }}
                        loading={loading}
                        id="clinic-dropdown"
                        options={clinicsForLab || []}
                        onChange={handleClinicChange}
                        value={(filters.clinic || { name: '' }) as Organization}
                        getOptionLabel={(option: Organization) => option?.name || ''}
                        renderOption={(props, option: Organization) => (
                            <li {...{ ...props, key: option?._id }}>
                                {option.name} &nbsp;
                                {!option.parentClinicId && !option.local && option.isMain ? (
                                    <i> ({translate('common:MAIN')})</i>
                                ) : undefined}
                            </li>
                        )}
                        isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                        renderInput={(params) => (
                            <TextField {...params} label={translate('common:CLINIC')} fullWidth={true} margin="dense" />
                        )}
                    />

                    {!isExternalTechnician ? (
                        <Autocomplete
                            sx={{ minWidth: 240 }}
                            loading={departmentsLoading}
                            id="department-dropdown"
                            options={(departments || []) as Department[]}
                            onChange={handleDepartmentChange}
                            value={filters.department}
                            getOptionLabel={(option: Department) => option?.name || ''}
                            isOptionEqualToValue={(option: Department, value: Department) => option?._id === value?._id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={translate('orders:DEPARTMENT')}
                                    fullWidth={true}
                                    margin="dense"
                                />
                            )}
                        />
                    ) : undefined}

                    <DeliveryScheduleStateFilter value={filters.finishedFilter} onChange={handleStateChange} />
                </Box>

                <Box display="flex" justifyContent="flex-end" alignItems="center" mr={1}>
                    {lateOrdersCount ? (
                        <Box mr={2}>
                            <Badge badgeContent={lateOrdersCount} color="info">
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="medium"
                                    onClick={() => onLateOrdersShow()}
                                    startIcon={<WarningIcon />}
                                >
                                    {translate('delivery-schedule:LATE_ORDERS')}
                                </Button>
                            </Badge>
                        </Box>
                    ) : undefined}
                    {!isExternal ? (
                        <Box>
                            <Badge badgeContent={noDeadlineOrdersCount} color="info">
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={() => onNoDeadlineShow()}
                                    startIcon={<EventNoteIcon />}
                                >
                                    {translate('delivery-schedule:ORDER_WITHOUT_DEADLINE')}
                                </Button>
                            </Badge>
                        </Box>
                    ) : undefined}
                </Box>
            </Box>
        </Fragment>
    );
};

export default DeliverySchedulePageActions;
